'use client';

import {useMemo} from 'react';
import {useClpCourse} from '../../../context/clp-course-provider.react-component';
import type {PreviewableLecture} from '@/types/lecture';
import type {AssetVideo} from '@/generated/graphql';
import {useLectureAssetIdsQuery} from '@/generated/graphql';
import {usePreviewableLectures} from './use-previewable-lectures';

/**
 * Returns an ordered list of previewable items — promo video first (if available), then curriculum
 * lectures. The asset field on each item is populated once the LectureAssetIds query resolves;
 * it will be undefined for curriculum lectures until then.
 *
 * durationInSeconds is derived from the lecture curriculum data, not the asset.
 */
export function usePreviewableAssets(): PreviewableLecture[] {
    const course = useClpCourse();
    const previewableLectures = usePreviewableLectures();

    const lectureIds = useMemo(
        () => previewableLectures.map((l) => l.id),
        [previewableLectures],
    );

    const {data: lectureAssetData} = useLectureAssetIdsQuery(
        {ids: lectureIds},
        {enabled: lectureIds.length > 0},
    );

    const {lectureAssetMap, lectureThumbnailMap} = useMemo(() => {
        const assetMap = new Map<string, AssetVideo>();
        const thumbnailMap = new Map<string, string | null>();
        lectureAssetData?.lectures?.forEach((lecture) => {
            if (!lecture) return;
            thumbnailMap.set(lecture.id, lecture.thumbnail ?? null);
            if (
                (lecture.__typename === 'VideoLecture' ||
                    lecture.__typename === 'VideoMashupLecture') &&
                lecture.asset?.__typename === 'AssetVideo'
            ) {
                assetMap.set(lecture.id, lecture.asset as AssetVideo);
            }
        });
        return {lectureAssetMap: assetMap, lectureThumbnailMap: thumbnailMap};
    }, [lectureAssetData]);

    return useMemo(() => {
        const items: PreviewableLecture[] = [];

        // Promo video first — asset is immediately available from course data.
        if (course.promoVideoAsset) {
            items.push({
                id: course.promoVideoAsset.id,
                title: course.title,
                durationInSeconds: Number(course.promoVideoAsset.duration) || 0,
                thumbnail: course.images?.height125 ?? null,
                asset: course.promoVideoAsset,
            });
        }

        // Curriculum lectures — thumbnail and asset populated from query once it resolves.
        previewableLectures.forEach((lecture) => {
            items.push({
                ...lecture,
                thumbnail: lectureThumbnailMap.has(lecture.id)
                    ? lectureThumbnailMap.get(lecture.id) ?? null
                    : lecture.thumbnail,
                asset: lectureAssetMap.get(lecture.id),
            });
        });

        return items;
    }, [course, previewableLectures, lectureAssetMap, lectureThumbnailMap]);
}
